<template>
  <div>
    <v-dialog
      v-model="willLanguageDialog"
      content-class="crud-dialog"
      persistent
      max-width="400px"
    >
      <v-card class="crud-card">
        <v-card-title class="crud-card-title">
          <v-row no-gutters>
            <v-col cols="11" class="text-left">
              <h2 class="text-h5">Select a language</h2>
            </v-col>
            <v-col cols="1">
              <v-icon class="close" color="black" @click.stop="closeDialog">{{
                icons.mdiClose
              }}</v-icon>
            </v-col>
          </v-row>
        </v-card-title>
        <v-divider />
        <v-card-text class="crud-card-text">
          <RadioSelect
            v-model="selectedLanguage"
            :items="languageOptions"
            :group="'will_language'"
            :flexColumn="true"
            :color="'primary'"
          />
          <div v-if="showEmailOption" class="email-copy-option">
            <Checkbox
              v-model="emailCopy"
              :label="'Share a copy of the Will with you and your client by email'"
              color="primary"
            />
          </div>
        </v-card-text>
        <v-card-actions class="crud-card-actions">
          <div class="button-container">
            <GradientButton
              @gradientButtonClicked="languageSelected"
              :title="'Download will'"
              :small="true"
              :fill="true"
              :disabled="!selectedLanguage"
              :loading="buttonLoading"
            />
          </div>
          <v-spacer />
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
  import { mdiClose } from '@mdi/js';
  import RadioSelect from '../components/RadioSelect.vue';

  export default {
    name: 'WillLanguageDialog',
    components: {
      RadioSelect,
    },
    props: {
      willLanguageDialog: {
        required: false,
        default: false,
        type: Boolean,
      },
      buttonLoading: {
        type: Boolean,
        default: false,
      },
      testator: {
        required: true,
        type: Object,
      },
      allowedLanguageCodes: {
        type: Array,
        default: null,
      },
      // When true, shows an "Email me a copy of the will" checkbox (defaults to
      // checked) beneath the language list. Only DownloadWill enables this, and
      // only while an adviser is controlling the testator's account.
      showEmailOption: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      const localeToValue = {
        en: 'ENGLISH',
        af: 'AFRIKAANS',
        xh: 'XHOSA',
        zu: 'ZULU',
        nso: 'SOTHO',
      };
      const userLocale = this.testator?.user?.locale;
      const defaultSelected =
        (userLocale && localeToValue[userLocale]) || 'ENGLISH';
      return {
        icons: {
          mdiClose: mdiClose,
        },
        selectedLanguage: defaultSelected,
        emailCopy: true,
        allLanguageOptions: [
          { value: 'ENGLISH', text: 'English', code: 'en' },
          { value: 'AFRIKAANS', text: 'Afrikaans', code: 'af' },
          { value: 'XHOSA', text: 'Xhosa', code: 'xh' },
          { value: 'ZULU', text: 'Zulu', code: 'zu' },
          { value: 'SOTHO', text: 'Sotho (Sepedi)', code: 'nso' },
        ],
      };
    },
    computed: {
      isMobile() {
        return this.$isMobileDevice();
      },
      languageOptions() {
        if (this.allowedLanguageCodes && this.allowedLanguageCodes.length > 0) {
          return this.allLanguageOptions.filter(opt =>
            this.allowedLanguageCodes.includes(opt.code)
          );
        }
        return this.allLanguageOptions;
      },
    },
    watch: {
      willLanguageDialog(open) {
        if (open) {
          // Not persisted anywhere: every time the dialog opens the email
          // option starts checked again.
          this.emailCopy = true;
        }
        if (open && this.languageOptions.length > 0) {
          const localeToValue = {
            en: 'ENGLISH',
            af: 'AFRIKAANS',
            xh: 'XHOSA',
            zu: 'ZULU',
            nso: 'SOTHO',
          };
          const userLocale = this.testator?.user?.locale;
          const match =
            (userLocale && localeToValue[userLocale]) ||
            this.languageOptions[0].value;
          const inList = this.languageOptions.find(o => o.value === match);
          this.selectedLanguage = inList
            ? match
            : this.languageOptions[0].value;
        }
      },
    },
    methods: {
      closeDialog() {
        this.selectedLanguage = null;
        this.$emit('willLanguageDialog', false);
      },
      languageSelected() {
        // Second argument is only meaningful when showEmailOption is on;
        // listeners that ignore it (PreviewWill) keep working unchanged.
        this.$emit('languageSelected', this.selectedLanguage, this.emailCopy);
      },
    },
  };
</script>

<style scoped lang="scss">
  .button-container {
    margin-bottom: 22px;
  }

  // Visually separate the email option from the language radio list
  .email-copy-option {
    margin-top: 32px;
    padding-top: 16px;
    border-top: 1px solid rgba(0, 0, 0, 0.12);

    ::v-deep .omk-checkbox-container {
      margin: 0;
    }

    ::v-deep .v-input--checkbox {
      margin-top: 0;
      padding-top: 0;
    }
  }

  // Override radio button styling to match the design
  ::v-deep .omk-radio-select-container {
    margin: 0;

    .omk-radio {
      margin-bottom: 16px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  // Vuetify radio button styling fallback
  ::v-deep .v-radio-group {
    .v-radio {
      margin-bottom: 16px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
</style>
